import React from "react";
import { createRoot } from "react-dom/client";
import html2pdf from "html2pdf.js";
import header from "../assets/img/header.jpg";
import { serverFormRequest } from "../util/server_request";
import { getCompanyHeaderInfo } from "../util/config";
import { formatMemberDate } from "../util/dateFormatter";

// Suppress common warnings
const originalConsoleWarn = console.warn;
console.warn = (...args) => {
  if (args[0] && typeof args[0] === 'string') {
    // Suppress PDF.js blob URL warnings
    if (args[0].includes('Invalid absolute docBaseUrl')) return;
    // Suppress Ant Design deprecation warnings
    if (args[0].includes('bodyStyle is deprecated')) return;
    if (args[0].includes('Tabs.TabPane` is deprecated')) return;
  }
  originalConsoleWarn.apply(console, args);
};

// TRANSACTIONS_PER_PAGE removed — pagination simplified to one page per year

const StatementHeader = ({ data }) => {
  const companyInfo = getCompanyHeaderInfo();
  
  return (
    <div style={{ 
      marginBottom: "8mm",
      pageBreakInside: "avoid",
      width: "100%"
    }}>
      <div style={{ 
        textAlign: "center", 
        marginBottom: "8mm"
      }}>
        <img 
          src={header} 
          alt="Header" 
          style={{ 
            width: "100%",
            maxWidth: "180mm",
            maxHeight: "15mm",
            height: "auto",
            objectFit: "contain",
            marginBottom: "3mm" 
          }} 
        />
      </div>

      <div style={{ 
        textAlign: "left", 
        marginBottom: "8mm", 
        lineHeight: "1.4",
        fontSize: "11px",
        fontFamily: '"Times New Roman", Times, serif'
      }}>
        <strong style={{ fontSize: "14px" }}>
          {companyInfo.name}
        </strong><br />
        {companyInfo.address}<br />
        {companyInfo.contact}
      </div>

      <div style={{ 
        textAlign: "left", 
        marginBottom: "8mm",
        borderBottom: "1px solid #ccc",
        paddingBottom: "3mm",
        paddingLeft: "5mm",
        background: "linear-gradient(to left, #69db7c, #0b6623)",
        color: "white",
        pageBreakInside: "avoid"
      }}>
        <strong style={{ textTransform: "uppercase", fontSize: "14px" }}>
          {data.schemeName || ""}
        </strong>
      </div>

      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "8mm",
        paddingBottom: "3mm",
        borderBottom: "1px solid #eee",
        fontSize: "11px",
        fontFamily: '"Times New Roman", Times, serif',
        textTransform: "uppercase",
        pageBreakInside: "avoid"
      }}>
        {/* Left Side: Period + Company Details */}
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "3mm" }}>
            <span style={{ fontWeight: "bold" }}>Year:</span> {data.year || "N/A"}
          </div>
          <div style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "3mm" }}>
            Employer Details
          </div>
          <div style={{ marginBottom: "1mm" }}><span style={{ fontWeight: "bold" }}>EMPLOYER:</span> {data.employerData?.employerName || "N/A"}</div>
          <div style={{ marginBottom: "1mm" }}><span style={{ fontWeight: "bold" }}>ERN:</span> {data.employerData?.employerNumber || "N/A"}</div>
          <div style={{ marginBottom: "1mm" }}><span style={{ fontWeight: "bold" }}>DATE JOINED:</span> {formatMemberDate(data.dateJoined) || "N/A"}</div>
        </div>
      </div>
    </div>
  );
};

const TransactionsTable = ({ transactions, showHeader = true, showTableHeader = true }) => (
  <div style={{ 
    width: "100%", 
    marginBottom: "10px", 
    paddingRight: "24px", 
    overflowX: "auto"
  }}>
    <table style={{ 
      width: "100%", 
      fontSize: "12px",
      fontFamily: '"Times New Roman", Times, serif'
    }}>
      {showTableHeader && (
        <thead>
          <tr style={{ background: "linear-gradient(to left, #69db7c, #0b6623)" }}>
            <th style={{ 
              padding: "8px 4px", 
              textAlign: "left", 
              color: "#fff",
              width: "20%"
            }}>
              TRANSACTION DATE
            </th>
            <th style={{ 
              padding: "8px 16px 8px 24px", 
              textAlign: "left", 
              color: "#fff",
              width: "50%"
            }}>
              DETAILS
            </th>
            <th style={{ 
              padding: "8px 6px", 
              textAlign: "right", 
              color: "#fff",
              width: "30%"
            }}>
              AMOUNT
            </th>
          </tr>
        </thead>
      )}
      <tbody>
        {transactions && transactions.length > 0 ? (
          transactions.map((tx, idx) => (
            <tr key={idx} style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <td style={{ 
                padding: "6px 4px", 
                textAlign: "left",
                wordBreak: "break-word"
              }}>
                {tx.transactionDate || 'N/A'}
              </td>
              <td style={{ 
                padding: "6px 16px 6px 24px", 
                textAlign: "left",
                wordBreak: "break-word"
              }}>
                {tx.narration || 'Transaction'}
              </td>
              <td style={{ 
                padding: "6px 6px", 
                textAlign: "right",
                wordBreak: "break-word"
              }}>
                {tx.amount || 0}
              </td>
            </tr>
          ))
        ) : (
          <tr style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
            <td colSpan="3" style={{ 
              textAlign: "center", 
              padding: "20px",
              fontStyle: "italic",
              color: "#666"
            }}>
              No transactions for this period
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const FinancialSummary = ({ data }) => (
  <div
    style={{
      textAlign: "right",
      maxWidth: "95%",
      marginRight: "0",
      paddingRight: "2px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      pageBreakInside: "avoid",
      breakInside: "avoid"
    }}
  >
    <div
      style={{
        marginTop: "15px",
        borderRadius: "5px",
        padding: "20px",
        overflowX: "auto",
        pageBreakInside: "avoid",
        breakInside: "avoid"
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto",
          columnGap: "20px",
          rowGap: "6px",
          marginBottom: "10px",
          fontSize: "14px",
          fontWeight: "normal",
          fontFamily: '"Times New Roman", Times, serif',
          pageBreakInside: "avoid",
          breakInside: "avoid"
        }}
      >
        <div style={{ fontWeight: "bold", paddingLeft: "0" }}>Total Contribution:</div>
        <div style={{ textAlign: "right" }}>{data.transactionData?.totalContribution || '0.00'}</div>

        <div style={{ fontWeight: "bold", paddingLeft: "0" }}>Transfer Received:</div>
        <div style={{ textAlign: "right" }}>{data.transactionData?.transferReceived || '0.00'}</div>

        <div style={{ fontWeight: "bold", paddingLeft: "0" }}>Total Redemption:</div>
        <div style={{ textAlign: "right" }}>{data.transactionData?.totalRedemption || '0.00'}</div>

        <div style={{ fontWeight: "bold", paddingLeft: "0" }}>Gain/Loss:</div>
        <div style={{ textAlign: "right" }}>{data.transactionData?.gainLoss || '0.00'}</div>

        <div style={{ fontWeight: "bold", fontSize: "14px", paddingLeft: "0" }}>Account Value:</div>
        <div
          style={{
            textAlign: "right",
            fontSize: "14px",
            fontWeight: "bold",
            borderTop: "1px solid #000",
            borderBottom: "1px solid #000",
            padding: "4px 0"
          }}
        >
          {data.transactionData?.accountValue || '0.00'}
        </div>

        <div style={{ fontWeight: "bold", paddingLeft: "0" }}>Total Units:</div>
        <div style={{ textAlign: "right" }}>{data.transactionData?.totalUnits || '0.00'}</div>

        <div style={{ fontWeight: "bold", paddingLeft: "0" }}>Current Price:</div>
        <div style={{ textAlign: "right" }}>{data.transactionData?.currentPrice || '0.00'}</div>
      </div>
    </div>
  </div>
);

const StatementPage = ({ 
  data, 
  transactions, 
  showHeader, 
  showSummary, 
  showOpeningBalance, 
  pageNumber, 
  totalPages,
  isLastYearPage
}) => {
  return (
    <>
      <div 
        data-year={data.year}
        data-page={pageNumber}
        data-total-pages={totalPages}
        style={{
          position: "relative",
          width: "210mm",
          // ✅ FIXED: Use flexible height instead of fixed 297mm
          height: "auto",
          minHeight: "200mm", // Reduced minimum height
          padding: "10mm", // Simple padding all around
          margin: "0 auto",
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: "12px",
          lineHeight: "1.4",
          color: "#000",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          pageBreakInside: "avoid",
          // ✅ FIXED: Prevent extra spacing
          overflow: "visible",
          display: "block"
        }}
      >
        {/* Header Section - Only on first page */}
        {showHeader && (
          <div style={{ marginBottom: "15mm" }}>
            <StatementHeader data={data} />
          </div>
        )}

        {/* Year Section - Always show, but styled differently if no header */}
        {!showHeader && (
          <div style={{
            textAlign: "left", 
            marginBottom: "10mm",
            borderBottom: "1px solid #ccc",
            paddingBottom: "3mm",
            paddingLeft: "5mm",
            background: "linear-gradient(to left, #69db7c, #0b6623)",
            color: "white",
            pageBreakInside: "avoid"
          }}>
            <strong style={{ textTransform: "uppercase", fontSize: "16px" }}>
              YEAR: {data.year}
            </strong>
          </div>
        )}

        {/* Opening Balance Section */}
        {showOpeningBalance && (
          <div style={{ marginBottom: "5mm" }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "3mm",
              fontSize: "13px",
              fontWeight: "bold",
              padding: "4px 16px",
            }}>
              <div>UNITS BF: {data.unitsBF || '0.00'}</div>
              <div style={{ marginRight: "24px" }}>ACCOUNT VALUE BF: {data.accountValueBF || '0.00'}</div>
            </div>
            <hr style={{ border: "1px solid #000", margin: "0 0 5mm 0" }} />
          </div>
        )}

        {/* Transactions Section */}
        <div style={{ 
          marginBottom: "10mm", // Reduced from 15mm
          width: "100%",
          // ✅ FIXED: Removed fixed minHeight that was causing empty space
        }}>
          <TransactionsTable 
            transactions={transactions} 
            showHeader={true}
            showTableHeader={true}
          />
          
          {/* Transaction count info */}
          <div style={{
            fontSize: "10px",
            color: "#666",
            textAlign: "right",
            marginTop: "3mm" // Reduced from 5mm
          }}>
            
          </div>
        </div>

        {/* Summary Section */}
        {showSummary && (
          <div style={{ 
            marginTop: "10mm", // Reduced from 15mm
            marginBottom: "5mm" // Reduced from 10mm
          }}>
            <hr style={{ border: "1px solid #000", margin: "10mm 0" }} />
            <FinancialSummary data={data} />
          </div>
        )}
      </div>
      {/* ✅ FIXED: Smarter page breaks with zero height */}
      {!isLastYearPage && (
        <div style={{ 
          pageBreakAfter: "always",
          height: "0", // Ensure break div has no height
          margin: "0",
          padding: "0"
        }} />
      )}
    </>
  );
};

const CompanyYearStatement = ({ yearData, isLastYear, yearIndex, totalYears }) => {
  const transactions = yearData.transactions || [];
  
  
  // Since years typically have manageable transaction counts, create just one page per year - no pagination needed
  return (
    <StatementPage
      key={`year-${yearData.year}`}
      data={yearData}
      transactions={transactions}
      showHeader={yearIndex === 0} // Only show header on first years
      showSummary={true}
      showOpeningBalance={true}
      pageNumber={yearIndex + 1} // Current year's page number
      totalPages={totalYears} // Total number of pages/years
      isLastYearPage={isLastYear}
    />
  );
};

const StatementOfAccountPDF = ({ data }) => {
  if (!data?.length) {
    return (
      <div style={{ 
        padding: "10mm",
        textAlign: "center",
        fontFamily: '"Times New Roman", Times, serif'
      }}>
        <h2>No Data Available</h2>
      </div>
    );
  }


  return (
    <div style={{
      width: "100%",
      fontFamily: '"Times New Roman", Times, serif'
    }}>
      {data.map((yearData, index) => {
        return (
          <CompanyYearStatement 
            key={`year-${yearData.year}-${index}`}
            yearData={yearData}
            yearIndex={index} // Pass current year index
            totalYears={data.length} // Pass total number of years
            isLastYear={index === data.length - 1}
          />
        );
      })}
    </div>
  );
};

const processApiResponse = (apiResponse) => {
  if (!apiResponse) {
    console.warn('⚠️ No API response provided');
    return [];
  }

  let responseData = [];

  // Flatten company-year structure
  if (apiResponse.success && apiResponse.data) {
    // If apiResponse.data is an array (already normalized), flatten all years from all companies
    const companyArray = Array.isArray(apiResponse.data) ? apiResponse.data : [apiResponse.data];
    companyArray.forEach(company => {
      if (Array.isArray(company.transactionData)) {
        company.transactionData.forEach(yearData => {
          const transactions = Array.isArray(yearData.transactions)
            ? yearData.transactions.map(tx => ({
                amount: tx.amount ?? 0,
                // Normalize transaction date using centralized UTC-aware formatter
                transactionDate: tx.transactionDate ? formatMemberDate(tx.transactionDate) : '',
                narration: tx.narration ?? '',
                payrollYear: tx.payrollYear ?? '',
              }))
            : [];
          responseData.push({
            schemeName: company.schemeName || 'OLD MUTUAL ASPIRE PENSION SCHEME',
            companyName: company.companyName || 'N/A',
            dateJoined: company.dateJoined || 'N/A',
            year: yearData.year,
            transactions,
            transactionData: {
              totalContribution: yearData.totalContribution || 0,
              transferReceived: yearData.transferReceived || 0,
              totalRedemption: yearData.totalRedemption || 0,
              gainLoss: yearData.gainLoss || 0,
              accountValue: yearData.accountValue || 0,
              totalUnits: yearData.totalUnits || 0,
              currentPrice: yearData.currentPrice || 0
            },
            unitsBF: yearData.unitBF || 0,
            accountValueBF: yearData.accountValueBF || 0,
            employerData: company.employerData || {
              employerName: company.companyName || 'N/A',
              employerNumber: 'N/A'
            },
            memberData: company.memberData || {
              dateJoined: company.dateJoined || 'N/A'
            }
          });
        });
      }
    });
  } else if (Array.isArray(apiResponse)) {
    responseData = apiResponse;
  } else if (typeof apiResponse === 'object') {
    responseData = [apiResponse];
  }

  // Log pagination info for each year and sort by year
  return responseData
    .filter(item => item.year && item.year !== "0") // Filter out year "0" entries
    .sort((a, b) => parseInt(b.year) - parseInt(a.year)) // Sort by year descending (newest first)
    .map((item) => item);
};

export const generateAndDownloadPDF = async (apiResponse, options = {}) => {
  const { autoSend = false, previewMode = false } = options;
  
  try {
    const processedData = processApiResponse(apiResponse);
    // Calculate total pages needed - one page per year now (simplified like member PDF)
    
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "-9999px";
    container.style.width = "210mm";
    // ✅ FIXED: Use auto height instead of fixed massive height
    container.style.height = "auto";
    // ✅ REMOVED: container.style.minHeight = `${processedData.length * 297}mm`;
    container.style.backgroundColor = "#fff";
    container.style.fontFamily = '"Times New Roman", Times, serif';
    container.style.fontSize = "12px";
    container.style.lineHeight = "1.4";
    container.style.color = "#000";
    container.style.boxSizing = "border-box";
    container.style.overflow = "visible"; // Ensure all content is visible
    document.body.appendChild(container);

    const root = createRoot(container);

    const pdfBlob = await new Promise((resolve, reject) => {
      try {
        root.render(
          <div id="pdf-content">
            <StatementOfAccountPDF data={processedData} />
          </div>
        );

        setTimeout(async () => {
          try {
            const element = document.getElementById("pdf-content");
            if (!element) throw new Error("PDF content element not found");

            // Wait additional time for multi-year content to fully render
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Verify all years are rendered (no runtime checks required here in production)

            // ✅ FIXED: Improved PDF options to prevent extra space
            const opt = {
              margin: [5, 5, 5, 5],
              filename: `Statement_${new Date().toISOString().slice(0, 10)}.pdf`,
              image: { type: "jpeg", quality: 0.95 },
              html2canvas: { 
                scale: 1.2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: "#ffffff",
                logging: false,
                scrollX: 0,
                scrollY: 0,
                letterRendering: true,
                // ✅ FIXED: Prevent capturing extra space
                windowWidth: 1200,
                windowHeight: 800,
                x: 0,
                y: 0,
              },
              jsPDF: { 
                unit: "mm", 
                format: "a4", 
                orientation: "portrait",
                putOnlyUsedFonts: true,
                hotfixes: ["px_scaling"],
                compress: true
              },
              pagebreak: { 
                mode: ['css'], // ✅ FIXED: Only CSS mode, removed 'legacy'
                avoid: ['tr', '.avoid-break', 'table', 'thead', 'tfoot'],
              }
            };

            const result = await html2pdf()
              .from(element)
              .set(opt)
              .outputPdf('blob');
            
            resolve(result);
          } catch (error) {
            console.error('❌ PDF generation error:', error);
            reject(error);
          }
        }, 3000); // Increased timeout for multi-year rendering
      } catch (error) {
        console.error('Render error:', error);
        reject(error);
      }
    });

    // Clean up DOM
    setTimeout(() => {
      root.unmount();
      document.body.removeChild(container);
    }, 100);

    // Handle different modes
    if (previewMode) {
      // Return blob for modal preview
      return pdfBlob;
    } else {
      // Auto-download behavior
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Statement_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    }

    if (autoSend) {
      await sendPdfToServer(pdfBlob, options);
    }

    return pdfBlob;

  } catch (error) {
    console.error('PDF generation failed:', error);
    throw error;
  }
};

const sendPdfToServer = async (pdfBlob, metadata = {}) => {
  try {
    
    const formData = new FormData();
    formData.append("statement", pdfBlob, `Statement_${new Date().toISOString().slice(0, 10)}.pdf`);
    formData.append("statement-type", "COMPANY_STATEMENT");
    formData.append("metadata", JSON.stringify(metadata));

   
    const res = await serverFormRequest("POST", "statements/send", formData);
    
    if (!res || res.success === false) {
      throw new Error(res?.message || "Failed to send PDF");
    }
    
    
    return res;
  } catch (err) {
    console.error("Error sending PDF to server:", err);
    throw err;
  }
};

export const useCompanyStatementReportPDF = () => {
  const generateAndSend = async (apiResponse, options = {}) => {
    try {
      return await generateAndDownloadPDF(apiResponse, options);
    } catch (error) {
      console.error('Error in generateAndSend:', error);
      throw error;
    }
  };

  const generateForPreview = async (apiResponse, options = {}) => {
    try {
      return await generateAndDownloadPDF(apiResponse, { ...options, previewMode: true });
    } catch (error) {
      console.error('Error generating PDF for preview:', error);
      throw error;
    }
  };

  const generateForDownload = async (apiResponse, options = {}) => {
    try {
      return await generateAndDownloadPDF(apiResponse, { ...options, previewMode: false });
    } catch (error) {
      console.error('Error generating PDF for download:', error);
      throw error;
    }
  };

  return {
    generateAndDownloadPDF,
    generateAndSend,
    generateForPreview,
    generateForDownload,
    sendPdfToServer
  };
};

export { generateAndDownloadPDF as default };